import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { renderToString } from 'react-dom/server';
import { useParams } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import { ArrowUpFromLine, Compass, Gauge, Crosshair, MapPin } from 'lucide-react';
import { PhaseOfMovement } from './PhaseOfMovement';
import { useGoogleMap } from './hooks/useGoogleMap';
import { createMainMarker, createBreadcrumbMarker, createTrailPolyline } from './components';
import { getStateColor, calculateHeading } from './utils/mapUtils';
import { MAP_CONFIG } from './config/mapConfig';
import { resolveLiveLinkApi, type LiveLinkApiVersion } from './config/liveLinkApi';

interface LiveLink {
  id: string;
  assetId: string;
  userId: string;
  groupId: string;
  created: string;
  updated: string;
  expiryTime: string;
  expireOnLanding: boolean;
}

interface Source {
  name: string;
  asset_id?: string;
  customer_id?: string;
  unit_id?: string;
  profile_name?: string;
  profile_id?: string;
  profile_type?: string;
  unit_sn?: string;
  network?: string;
  movement?: PhaseOfMovement | number;
  status?: string;
  state?: string;
  gps_status?: string;
  date_time?: string;
  rcv_time: string;
  latency?: string;
  latest_update?: boolean;
}

interface Location {
  date_time: string;
  rcv_time: string;
  pos_valid: boolean;
  latitude: number;
  longitude: number;
  gps_status?: string;
  speed?: number;
  cog?: number;
  altitude?: number;
}

interface Report {
  source: Source;
  location: Location;
  location_history?: Location[];
  status: string;
  warning: boolean;
  audio: boolean;
  vehicle_id?: string;
}

// Lightweight report with only location data (for memory optimization)
// Following vehicles.ts BasicEntry pattern
interface BasicReport {
  source: Source;
  location: Location;
  status: string;
  warning: boolean;
  audio: boolean;
}

interface FormatPreferences {
  speedFormat: 'kts' | 'mph' | 'kph';
  altitudeFormat: 'ft' | 'm';
  dateFormat: 'MDY' | 'DMY' | 'YMD' | 'DMYLong';
  timeFormat: 'HHMM' | 'AMPM' | 'TWENTYFOURH';
}

interface FormatMenuState {
  speed: boolean;
  altitude: boolean;
  lastUpdate: boolean;
  linkExpires: boolean;
}

// No limit on breadcrumbs - keep all historical positions

// Unit conversion constants
const SPEED_CONVERSIONS = {
  kts: 1,
  mph: 1.15078,
  kph: 1.852,
} as const;

const ALTITUDE_CONVERSIONS = {
  ft: 1,
  m: 0.3048,
} as const;

const SPEED_UNITS = {
  kts: { full: 'kts', abbreviated: 'kts' },
  mph: { full: 'mph', abbreviated: 'mph' },
  kph: { full: 'km/h', abbreviated: 'kph' },
} as const;

const ALTITUDE_UNITS = {
  ft: { full: 'ft', abbreviated: 'ft' },
  m: { full: 'm', abbreviated: 'm' },
} as const;

interface LiveLinkViewerProps {
  /** Links created in v3 (/v3/{id}) are read from the v3 API; see config/liveLinkApi.ts. */
  apiVersion?: LiveLinkApiVersion;
}

const LiveLinkViewer: React.FC<LiveLinkViewerProps> = ({ apiVersion = 'v2' }) => {
  // Read from runtime config (injected by Docker) or fall back to build-time env var
  const liveLinkApi = resolveLiveLinkApi(apiVersion, window.RUNTIME_CONFIG, import.meta.env);
  const { liveLinkId } = useParams<{ liveLinkId: string }>();

  const [liveLink, setLiveLink] = useState<LiveLink | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [reportHistory, setReportHistory] = useState<(Report | BasicReport)[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(false);
  const [wasInitiallyExpired, setWasInitiallyExpired] = useState(false);
  const [formatPreferences, setFormatPreferences] = useState<FormatPreferences>({
    speedFormat: 'kts',
    altitudeFormat: 'ft',
    dateFormat: 'MDY',
    timeFormat: 'HHMM'
  });
  const [formatMenuOpen, setFormatMenuOpen] = useState<FormatMenuState>({
    speed: false,
    altitude: false,
    lastUpdate: false,
    linkExpires: false
  });

  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mainMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const breadcrumbMarkersRef = useRef<Map<string, google.maps.marker.AdvancedMarkerElement>>(new Map());
  const trailPolylinesRef = useRef<google.maps.Polyline[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const formatPreferencesRef = useRef<FormatPreferences>(formatPreferences);
  const reportRef = useRef<Report | null>(report);

  // Check for missing API configuration
  if (!liveLinkApi.baseUrl) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: 'sans-serif'
      }}>
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '32px 48px',
          borderRadius: '12px',
          border: '1px solid #f5c6cb',
          textAlign: 'center',
          maxWidth: '500px'
        }}>
          <h2 style={{ marginTop: 0, marginBottom: '16px' }}>Configuration Error</h2>
          <p style={{ marginBottom: 0 }}>
            API base URL is not configured. Please check your environment configuration.
          </p>
        </div>
      </div>
    );
  }

  // Initialize Google Map
  const { map, isFollowing, setIsFollowing, panTo } = useGoogleMap(mapContainerRef, {
    center: MAP_CONFIG.DEFAULT_CENTER,
    zoom: MAP_CONFIG.DEFAULT_ZOOM,
    showMapTypeSelector: MAP_CONFIG.SHOW_MAP_TYPE_SELECTOR,
    mapType: MAP_CONFIG.DEFAULT_MAP_TYPE
  });

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.value-with-format')) {
        setFormatMenuOpen({
          speed: false,
          altitude: false,
          lastUpdate: false,
          linkExpires: false
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Keep format preferences ref up to date
  useEffect(() => {
    formatPreferencesRef.current = formatPreferences;
  }, [formatPreferences]);

  // Keep report ref up to date
  useEffect(() => {
    reportRef.current = report;
  }, [report]);

  // Update open InfoWindow when format preferences change
  useEffect(() => {
    // Check if InfoWindow exists and has a position (which indicates it's open)
    if (infoWindowRef.current) {
      const position = infoWindowRef.current.getPosition();
      if (!position) return;

      // Determine if this is the main marker or a breadcrumb by checking position
      const currentReport = reportRef.current;
      if (!currentReport) return;

      // Check if it's the main marker position
      const isMainMarker =
        Math.abs(position.lat() - currentReport.location.latitude) < 0.000001 &&
        Math.abs(position.lng() - currentReport.location.longitude) < 0.000001;

      if (isMainMarker) {
        // Update main marker InfoWindow
        const content = createInfoWindowContent(
          currentReport.location.speed,
          currentReport.location.altitude,
          { lat: position.lat(), lng: position.lng() },
          currentReport.location.rcv_time
        );
        infoWindowRef.current.setContent(content);
      } else {
        // It's a breadcrumb - find the matching report in history
        const breadcrumbReport = reportHistory.find(r =>
          Math.abs(r.location.latitude - position.lat()) < 0.000001 &&
          Math.abs(r.location.longitude - position.lng()) < 0.000001
        );

        if (breadcrumbReport) {
          const content = createInfoWindowContent(
            breadcrumbReport.location.speed,
            breadcrumbReport.location.altitude,
            { lat: position.lat(), lng: position.lng() },
            breadcrumbReport.location.rcv_time
          );
          infoWindowRef.current.setContent(content);
        }
      }
    }
  }, [formatPreferences, reportHistory]);

  // Fetch live link on mount
  useEffect(() => {
    if (!liveLinkId) {
      setError('No live link ID provided');
      setLoading(false);
      return;
    }

    fetchLiveLink(liveLinkId);
  }, [liveLinkId]);

  // Expiry checking
  useEffect(() => {
    if (!liveLink) return;

    const checkExpiry = () => {
      if (new Date(liveLink.expiryTime) < new Date()) {
        setIsExpired(true);
        if (connectionRef.current) {
          connectionRef.current.stop();
          connectionRef.current = null;
        }
      }
    };

    const expiryInterval = setInterval(checkExpiry, 1000);

    return () => {
      clearInterval(expiryInterval);
    };
  }, [liveLink]);

  // Check if aircraft is in flight and handle expiry on landing
  useEffect(() => {
    if (!report?.source || !liveLink?.expireOnLanding || isExpired) return;

    const movement = report.source.movement;

    const isInFlight = movement === PhaseOfMovement.TakeOff ||
                      movement === PhaseOfMovement.Ascending ||
                      movement === PhaseOfMovement.LevelFlight ||
                      movement === PhaseOfMovement.Descending ||
                      movement === PhaseOfMovement.Landing ||
                      movement === PhaseOfMovement.Hover ||
                      movement === PhaseOfMovement.Moving;

    if (!isInFlight) {
      setIsExpired(true);
      if (connectionRef.current) {
        connectionRef.current.stop();
        connectionRef.current = null;
      }
    }
  }, [report?.source?.movement, liveLink?.expireOnLanding, isExpired]);

  // Setup SignalR connection
  useEffect(() => {
    if (liveLink?.assetId && !connectionRef.current) {
      fetchAssetReport();
      setupSignalRConnection();
    }

    return () => {
      if (connectionRef.current) {
        console.warn('SignalR connection dropped');
        connectionRef.current.stop();
        connectionRef.current = null;
      }
    };
  }, [liveLink?.assetId, liveLinkId]);

  // Update report history for breadcrumbs
  // Following vehicles.ts pattern: breadcrumbs only appear when we have 2+ entries
  useEffect(() => {
    if (!report) return;

    setReportHistory(prev => {
      // If this is the first report and it has location_history, initialize with that
      // Following pattern from HubDataServices.cs:465-520 and vehicles.ts:500-522
      if (prev.length === 0 && report.location_history && report.location_history.length > 0) {
        // Create BasicReport objects from location_history (memory optimization)
        // Set historical positions to LevelFlight since they represent the trail
        const historicalReports: BasicReport[] = report.location_history.map(loc => ({
          source: {
            ...report.source,
            movement: PhaseOfMovement.LevelFlight, // Use consistent movement for historical breadcrumbs
          },
          location: loc,
          status: report.status,
          warning: report.warning,
          audio: report.audio,
        }));

        // Add current report at the end (must be last - vehicles.ts pattern)
        const allEntries = [...historicalReports, report];

        // Keep all entries - no limit
        return allEntries;
      }

      // Otherwise, just add the new report to history
      const updated = [...prev, report];
      // Keep all entries - no limit
      return updated;
    });
  }, [report?.location.rcv_time]);

  // Main marker management
  // Following vehicles.ts:145-146 displayMarker is called first
  useEffect(() => {
    if (!map || !report) return;

    const updateMarker = async () => {
      const position = {
        lat: report.location.latitude,
        lng: report.location.longitude
      };
      const heading = calculateHeading(report.location.cog);
      const color = getStateColor(report.source.movement as PhaseOfMovement);

      if (mainMarkerRef.current) {
        // Update existing marker position
        mainMarkerRef.current.position = position;

        // Update marker content (heading and color) WITHOUT retriggering animations
        const content = mainMarkerRef.current.content as HTMLElement;
        if (content) {
          const markerDiv = content.querySelector('.compass-marker') as HTMLElement;
          if (markerDiv) {
            // Update rotation - use direct style update to avoid CSS transition
            markerDiv.style.transform = `rotate(${heading}deg)`;
            markerDiv.style.backgroundColor = color;
          }
        }
      } else {
        // Create new marker
        mainMarkerRef.current = await createMainMarker(map, { position, heading, color });

        // Add click event to show InfoWindow for main marker
        mainMarkerRef.current.addListener('click', () => {
          if (!infoWindowRef.current) {
            infoWindowRef.current = new google.maps.InfoWindow({
              pixelOffset: new google.maps.Size(0, -10)
            });
          }

          // Get current report data at click time from ref
          const currentReport = reportRef.current;
          if (!currentReport) return;

          const currentPosition = {
            lat: currentReport.location.latitude,
            lng: currentReport.location.longitude
          };

          const content = createInfoWindowContent(
            currentReport.location.speed,
            currentReport.location.altitude,
            currentPosition,
            currentReport.location.rcv_time
          );

          infoWindowRef.current.setContent(content);
          infoWindowRef.current.setPosition(currentPosition);
          infoWindowRef.current.open(map);
        });
      }
    };

    updateMarker();

    return () => {
      if (mainMarkerRef.current && !report) {
        mainMarkerRef.current.map = null;
        mainMarkerRef.current = null;
      }
    };
  }, [map, report?.location.latitude, report?.location.longitude, report?.location.cog, report?.source.movement]);

  // Breadcrumbs management - incremental plotting
  // Following vehicles.ts:263-318 pattern
  useEffect(() => {
    if (!map) return;

    const updateBreadcrumbs = async () => {
      // Early exit: need at least 2 entries (one for marker, one for breadcrumb)
      // Following vehicles.ts:229-232
      if (reportHistory.length < 2) {
        return;
      }

      // Create breadcrumbs (exclude the last one, it's the main marker)
      // Following vehicles.ts:263-310
      const breadcrumbReports = reportHistory.slice(0, -1);

      for (const histReport of breadcrumbReports) {
        // Generate unique ID from timestamp (vehicles.ts:269-270)
        const reportTime = histReport.location.rcv_time;
        const reportTimestamp = Math.round(new Date(reportTime).getTime() / 1000).toString();
        const breadcrumbId = `breadcrumb-${reportTimestamp}`;

        // Do not plot the breadcrumb if it's already been plotted (vehicles.ts:272-275)
        if (breadcrumbMarkersRef.current.has(breadcrumbId)) {
          continue;
        }

        const position = {
          lat: histReport.location.latitude,
          lng: histReport.location.longitude
        };
        const heading = calculateHeading(histReport.location.cog);
        const color = getStateColor(histReport.source.movement as PhaseOfMovement);

        const marker = await createBreadcrumbMarker(map, { position, heading, color });

        // Add click event to show InfoWindow (vehicles.ts:291-306)
        marker.addListener('click', () => {
          if (!infoWindowRef.current) {
            infoWindowRef.current = new google.maps.InfoWindow({
              pixelOffset: new google.maps.Size(0, 10)
            });
          }

          const content = createInfoWindowContent(
            histReport.location.speed,
            histReport.location.altitude,
            position,
            reportTime
          );

          infoWindowRef.current.setContent(content);
          infoWindowRef.current.setPosition(position);
          infoWindowRef.current.open(map, marker);
        });

        breadcrumbMarkersRef.current.set(breadcrumbId, marker);
      }

      // Keep all breadcrumbs - no cleanup/removal
    };

    updateBreadcrumbs();
  }, [map, reportHistory]);

  // Trail polyline management
  useEffect(() => {
    if (!map || reportHistory.length < 2) return;

    // Clear old trail polylines (both shadow and main)
    trailPolylinesRef.current.forEach(polyline => {
      polyline.setMap(null);
    });
    trailPolylinesRef.current = [];

    // Create trail from history
    const positions = reportHistory.map(r => ({
      lat: r.location.latitude,
      lng: r.location.longitude
    }));

    const color = report ? getStateColor(report.source.movement as PhaseOfMovement) : 'green';
    trailPolylinesRef.current = createTrailPolyline(map, positions, color);

    return () => {
      trailPolylinesRef.current.forEach(polyline => {
        polyline.setMap(null);
      });
      trailPolylinesRef.current = [];
    };
  }, [map, reportHistory.length, report?.source.movement]);

  // Following mode - pan to aircraft location
  useEffect(() => {
    if (isFollowing && report && map) {
      panTo({
        lat: report.location.latitude,
        lng: report.location.longitude
      });
    }
  }, [report?.location.latitude, report?.location.longitude, isFollowing, map, panTo]);

  // Update document title with asset info
  useEffect(() => {
    if (report?.source.name) {
      const speedValue = report.location.speed || 0;
      const altitudeValue = report.location.altitude || 0;

      // Format speed (abbreviated: no spaces, no decimals)
      const speedConversion = SPEED_CONVERSIONS[formatPreferences.speedFormat];
      const speedUnit = SPEED_UNITS[formatPreferences.speedFormat].abbreviated;
      const speedStr = `${Math.round(speedValue * speedConversion)}${speedUnit}`;

      // Format altitude (abbreviated: no spaces, no decimals)
      const altitudeConversion = ALTITUDE_CONVERSIONS[formatPreferences.altitudeFormat];
      const altitudeUnit = ALTITUDE_UNITS[formatPreferences.altitudeFormat].abbreviated;
      const altitudeStr = `${Math.round(altitudeValue * altitudeConversion)}${altitudeUnit}`;

      document.title = `${report.source.name} ${speedStr} ${altitudeStr}`;
    } else {
      document.title = 'Outerlink Live Link';
    }
  }, [report?.source.name, report?.location.speed, report?.location.altitude, formatPreferences.speedFormat, formatPreferences.altitudeFormat]);

  const fetchLiveLink = async (id: string) => {
    try {
      const response = await fetch(liveLinkApi.liveLinkUrl(id));
      if (!response.ok) {
        if (response.status === 410) {
          throw new Error('Live link has expired');
        }
        throw new Error('Live link not found');
      }
      const data = await response.json();

      const expiryTime = new Date(data.expiryTime);
      if (expiryTime < new Date()) {
        setIsExpired(true);
        setWasInitiallyExpired(true);
      }

      setLiveLink(data);
    } catch (err) {
      setError('We were unable to connect to the live link. Please check the link and try again.');
      // setError(err instanceof Error ? err.message : 'Failed to fetch live link');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssetReport = async () => {
    if (!liveLink?.assetId || !liveLinkId) return;

    try {
      const response = await fetch(liveLinkApi.reportUrl(liveLinkId, liveLink.assetId));
      if (response.ok) {
        const reportData = await response.json();
        setReport(reportData);
      } else {
        console.error('Failed to fetch asset report:', response.status, response.statusText);
      }
    } catch (err) {
      console.error('Failed to fetch asset report:', err);
    }
  };

  const setupSignalRConnection = async () => {
    if (!liveLink?.assetId || !liveLinkId || connectionRef.current || isExpired) return;

    try {
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(liveLinkApi.hubUrl)
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.Information)
        .build();

      const currentAssetId = liveLink.assetId;

      connection.on('AssetUpdate', (assetId: string, reportData: Report) => {

        if (isExpired) {
          console.warn('Link is expired, ignoring AssetUpdate');
          return;
        }

        if (assetId === currentAssetId) {
          flushSync(() => {
            setReport(reportData);
          });
        }
      });

      connection.on('Error', (message: string) => {
        console.error('LiveLink SignalR error:', message);
        setError(message);
      });

      connection.onreconnecting(() => {
        console.warn('SignalR connection lost, attempting to reconnect...');
      });

      connection.onreconnected(() => {
        connection.invoke('JoinLiveLink', liveLinkId).catch(err => {
          console.error('Failed to rejoin live link after reconnection:', err);
        });
      });

      connection.onclose(() => {
        console.warn('SignalR connection closed');
      });

      await connection.start();

      await connection.invoke('JoinLiveLink', liveLinkId);

      connectionRef.current = connection;
    } catch (err) {
      console.error('Failed to setup LiveLink SignalR connection:', err);
    }
  };

  const formatSpeed = (speed: number | undefined, abbreviated: boolean = false): string => {
    const speedValue = speed || 0;
    const format = formatPreferencesRef.current.speedFormat;
    const conversion = SPEED_CONVERSIONS[format];
    const unit = abbreviated ? SPEED_UNITS[format].abbreviated : SPEED_UNITS[format].full;
    const convertedValue = speedValue * conversion;

    if (abbreviated) {
      return `${Math.round(convertedValue)}${unit}`;
    }
    return `${convertedValue.toFixed(1)} ${unit}`;
  };

  const formatAltitude = (altitude: number | undefined, abbreviated: boolean = false): string => {
    const altitudeValue = altitude || 0;
    const format = formatPreferencesRef.current.altitudeFormat;
    const conversion = ALTITUDE_CONVERSIONS[format];
    const unit = abbreviated ? ALTITUDE_UNITS[format].abbreviated : ALTITUDE_UNITS[format].full;
    const convertedValue = altitudeValue * conversion;

    if (abbreviated) {
      return `${Math.round(convertedValue)}${unit}`;
    }
    return `${convertedValue.toFixed(0)} ${unit}`;
  };

  const formatDateTime = (dateTime: string, includeDate: boolean = true): string => {
    const date = new Date(dateTime);
    let formattedString = '';

    if (includeDate) {
      switch (formatPreferencesRef.current.dateFormat) {
        case 'DMY':
          formattedString += date.toLocaleDateString('en-GB');
          break;
        case 'YMD':
          formattedString += date.toLocaleDateString('sv-SE');
          break;
        case 'DMYLong':
          formattedString += date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
          });
          break;
        case 'MDY':
        default:
          formattedString += date.toLocaleDateString('en-US');
          break;
      }
      formattedString += ' ';
    }

    switch (formatPreferencesRef.current.timeFormat) {
      case 'AMPM':
        formattedString += date.toLocaleTimeString('en-US', {
          hour12: true,
          hour: 'numeric',
          minute: '2-digit'
        });
        break;
      case 'TWENTYFOURH':
        formattedString += date.toLocaleTimeString('en-GB', {
          hour12: false,
          hour: '2-digit',
          minute: '2-digit'
        });
        break;
      case 'HHMM':
      default:
        formattedString += date.toLocaleTimeString('en-GB', {
          hour12: false,
          hour: '2-digit',
          minute: '2-digit'
        }).replace(':', '');
        break;
    }

    return formattedString.trim();
  };

  const toggleFormatMenu = (menuType: keyof FormatMenuState) => {
    setFormatMenuOpen(prev => {
      const newState = {
        speed: false,
        altitude: false,
        lastUpdate: false,
        linkExpires: false
      };
      newState[menuType] = !prev[menuType];
      return newState;
    });
  };

  const cycleSpeedFormat = () => {
    setFormatPreferences(prev => {
      const formats: ('kts' | 'mph' | 'kph')[] = ['kts', 'mph', 'kph'];
      const currentIndex = formats.indexOf(prev.speedFormat);
      const nextIndex = (currentIndex + 1) % formats.length;
      const newPrefs = { ...prev, speedFormat: formats[nextIndex] };
      // Update ref immediately so formatting functions use the new value
      formatPreferencesRef.current = newPrefs;
      return newPrefs;
    });
  };

  const cycleAltitudeFormat = () => {
    setFormatPreferences(prev => {
      const formats: ('ft' | 'm')[] = ['ft', 'm'];
      const currentIndex = formats.indexOf(prev.altitudeFormat);
      const nextIndex = (currentIndex + 1) % formats.length;
      const newPrefs = { ...prev, altitudeFormat: formats[nextIndex] };
      // Update ref immediately so formatting functions use the new value
      formatPreferencesRef.current = newPrefs;
      return newPrefs;
    });
  };

  const focusOnAircraft = () => {
    if (map && report?.location) {
      panTo({
        lat: report.location.latitude,
        lng: report.location.longitude
      });
      setIsFollowing(true);
    }
  };

  const toggleMapLock = () => {
    setIsFollowing(!isFollowing);
  };

  /**
   * Create InfoWindow content HTML for a report
   * Reusable function to avoid duplication across main marker and breadcrumbs
   */
  const createInfoWindowContent = (
    speed: number | undefined,
    altitude: number | undefined,
    position: { lat: number; lng: number },
    timestamp: string
  ): HTMLDivElement => {
    const formattedSpeed = formatSpeed(speed);
    const formattedAltitude = formatAltitude(altitude);
    const formattedTime = formatDateTime(timestamp);

    const content = document.createElement('div');
    content.style.cssText = 'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif; min-width: 200px;';

    // Speed and Altitude row - side by side
    const metricsRow = document.createElement('div');
    metricsRow.style.cssText = 'display: flex; gap: 16px; margin-bottom: 8px;';

    // Speed - use same Gauge icon as in asset-info-panel
    const gaugeIcon = renderToString(<Gauge size={16} color="rgba(255, 255, 255, 0.8)" strokeWidth={2} />);
    const speedSpan = `<div style="display: flex; align-items: center; gap: 8px;">${gaugeIcon}<span style="color: rgba(255, 255, 255, 0.95); font-size: 14px; font-weight: 500;">${formattedSpeed}</span></div>`;

    // Altitude - use same ArrowUpFromLine icon as in asset-info-panel
    const altitudeIcon = renderToString(<ArrowUpFromLine size={16} color="rgba(255, 255, 255, 0.8)" strokeWidth={2} />);
    const altitudeSpan = `<div style="display: flex; align-items: center; gap: 8px;">${altitudeIcon}<span style="color: rgba(255, 255, 255, 0.95); font-size: 14px; font-weight: 500;">${formattedAltitude}</span></div>`;

    metricsRow.innerHTML = speedSpan + altitudeSpan;
    content.appendChild(metricsRow);

    // Coordinates with MapPin icon
    const coordsDiv = document.createElement('div');
    coordsDiv.style.cssText = 'display: flex; align-items: center; gap: 8px; font-size: 11px; color: rgba(255, 255, 255, 0.6); margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid rgba(255, 255, 255, 0.15);';
    const mapPinIcon = renderToString(<MapPin size={14} color="rgba(255, 255, 255, 0.6)" strokeWidth={2} />);
    coordsDiv.innerHTML = `${mapPinIcon}<span>${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}</span>`;
    content.appendChild(coordsDiv);

    // Time
    const timeDiv = document.createElement('div');
    timeDiv.style.cssText = 'font-size: 11px; color: rgba(255, 255, 255, 0.7); text-align: center; letter-spacing: 0.3px;';
    timeDiv.textContent = formattedTime;
    content.appendChild(timeDiv);

    return content;
  };

  return (
    <div className="live-link-viewer">
      {/* Loading overlay - shows while fetching live link data */}
      {loading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '32px 48px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          zIndex: 2000,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '20px', fontWeight: '500', marginBottom: '8px' }}>
            Loading Live Link...
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            {liveLinkId}
          </div>
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '32px 48px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          zIndex: 2000,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '20px', fontWeight: '500', color: '#dc3545', marginBottom: '8px' }}>
            Unable to process your request
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            {error}
          </div>
        </div>
      )}

      {/* Initially expired overlay */}
      {wasInitiallyExpired && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '40px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          zIndex: 2000,
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <h2 style={{ color: '#dc3545', marginBottom: '16px', marginTop: 0 }}>Link Expired</h2>
          <p style={{ color: '#6c757d', fontSize: '16px', margin: '0 0 12px 0' }}>
            This live link has expired and is no longer available.
          </p>
          {liveLink && (
            <p style={{ color: '#6c757d', fontSize: '14px', margin: 0 }}>
              Expired: {new Date(liveLink.expiryTime).toLocaleString()}
            </p>
          )}
        </div>
      )}

      {/* Runtime expired banner */}
      {isExpired && !wasInitiallyExpired && (
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#ff6b6b',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          fontWeight: 'bold',
          zIndex: 1000,
          boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
        }}>
          Link has expired
        </div>
      )}

      {report?.location && (
        <div className={`asset-info-panel text-${MAP_CONFIG.TEXT_COLOR}`} style={{ opacity: isExpired ? 0.7 : 1 }}>
          <div className="asset-header">
              <h3 className="aircraft-name-clickable" onClick={focusOnAircraft} title="Click to focus on aircraft">
                {report.source.name}
              </h3>
              <button
                className={`focus-button ${isFollowing ? 'active' : 'inactive'}`}
                onClick={toggleMapLock}
                title={isFollowing ? "Map is following aircraft - click to unlock" : "Map is unlocked - click to follow aircraft"}
              >
                <Crosshair />
              </button>
            </div>

          <div className="metrics-row">
            <div className="metric-item clickable-metric" onClick={cycleSpeedFormat} title="Click to change speed format">
              <div className="metric-icon">
                <Gauge />
              </div>
              <div className="metric-value-clickable">
                <span className="value">{formatSpeed(report.location.speed)}</span>
              </div>
            </div>

            <div className="metric-item clickable-metric" onClick={cycleAltitudeFormat} title="Click to change altitude format">
              <div className="metric-icon">
                <ArrowUpFromLine />
              </div>
              <div className="metric-value-clickable">
                <span className="value">{formatAltitude(report.location.altitude)}</span>
              </div>
            </div>

            <div className="metric-item">
              <div className="metric-icon">
                <Compass />
              </div>
              <div className="metric-value-clickable">
                <span className="value">{report.location.cog || 0}°</span>
              </div>
            </div>
          </div>

          <div className="updated-footer">
            <div className="value-with-format">
              <div className="value-container">
                <span className="updated-label strong"><strong>AXIS</strong> Live Link Expires:</span>
                <span className="updated-value">{liveLink ? formatDateTime(liveLink.expiryTime) : 'Unknown'}</span>
                <button
                  className="format-menu-button"
                  onClick={() => toggleFormatMenu('linkExpires')}
                  title="Change date/time format"
                >
                  ⋮
                </button>
              </div>
              {formatMenuOpen.linkExpires && (
                <div className="format-menu">
                  <div className="format-selectors">
                    <select
                      className="format-selector"
                      value={formatPreferences.dateFormat}
                      onChange={(e) => setFormatPreferences(prev => {
                        const newPrefs = {
                          ...prev,
                          dateFormat: e.target.value as 'MDY' | 'DMY' | 'YMD' | 'DMYLong'
                        };
                        // Update ref immediately so formatting functions use the new value
                        formatPreferencesRef.current = newPrefs;
                        return newPrefs;
                      })}
                    >
                      <option value="MDY">MM/DD/YYYY</option>
                      <option value="DMY">DD/MM/YYYY</option>
                      <option value="YMD">YYYY/MM/DD</option>
                      <option value="DMYLong">DD Month YYYY</option>
                    </select>
                    <select
                      className="format-selector"
                      value={formatPreferences.timeFormat}
                      onChange={(e) => setFormatPreferences(prev => {
                        const newPrefs = {
                          ...prev,
                          timeFormat: e.target.value as 'HHMM' | 'AMPM' | 'TWENTYFOURH'
                        };
                        // Update ref immediately so formatting functions use the new value
                        formatPreferencesRef.current = newPrefs;
                        return newPrefs;
                      })}
                    >
                      <option value="HHMM">Military</option>
                      <option value="AMPM">AM/PM</option>
                      <option value="TWENTYFOURH">24H</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        ref={mapContainerRef}
        className="google-map-container"
        style={{
          height: '100vh',
          width: '100%',
          filter: isExpired ? 'grayscale(1)' : 'none'
        }}
      />
    </div>
  );
};

export default LiveLinkViewer;
